import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import { useMode } from '../contexts/ModeContext';
import { ArrowLeft, TrendingUp, AlertTriangle, Trophy, Target } from 'lucide-react';

const PERIODS = [
  { value: 'day', label: 'Bugün' },
  { value: 'week', label: 'Bu Hafta' },
  { value: 'month', label: 'Bu Ay' },
  { value: 'all', label: 'Tüm Zamanlar' },
];

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="p-5 bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid={`progress-stat-${label}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-sm font-medium text-stone-600">{label}</span>
    </div>
    <div className="text-3xl font-bold text-stone-900">{value}</div>
    {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
  </div>
);

const ItemList = ({ title, items, color, icon: Icon, emptyText, kids }) => (
  <div className={`p-5 rounded-2xl border ${color.bg} ${color.border}`} data-testid={`progress-list-${title}`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className={`w-5 h-5 ${color.icon}`} />
      <h3 className={`font-bold ${kids ? 'font-kids' : 'font-elegant'} ${color.text}`}>{title}</h3>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-stone-500">{emptyText}</p>
    ) : (
      <ul className="space-y-2">
        {items.map((it, idx) => (
          <li key={idx} className="flex items-center justify-between p-2 bg-white/70 rounded-lg">
            <div className="text-2xl font-arabic" dir="rtl">{it.expected}</div>
            <div className="text-right">
              <div className={`text-sm font-bold ${color.text}`}>%{it.accuracy}</div>
              <div className="text-xs text-stone-500">{it.correct}/{it.attempts}</div>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

function VoiceProgress() {
  const navigate = useNavigate();
  const { isKidsMode } = useMode();
  const [period, setPeriod] = useState('week');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/elifba/voice-stats', { params: { period } });
      setStats(res.data);
    } catch (e) {
      toast.error('İstatistikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'linear-gradient(135deg, #FEF3C7 0%, #DBEAFE 50%, #E9D5FF 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)',
      }}
      data-testid="progress-page"
    >
      <header className="px-6 py-4 bg-white/60 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
            data-testid="progress-back"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Ana Sayfa</span>
          </button>
          <h1 className={`text-2xl font-bold ${isKidsMode ? 'font-kids text-green-600' : 'font-elegant text-emerald-900'}`}>
            Sesli Tekrar İlerlemesi
          </h1>
          <div className="w-24"></div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        {/* Period selector */}
        <div className="flex flex-wrap gap-2" data-testid="progress-periods">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
                period === p.value
                  ? 'bg-emerald-700 text-white'
                  : 'bg-white border border-stone-200 text-stone-700 hover:border-emerald-300'
              }`}
              data-testid={`progress-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-12 text-center" data-testid="progress-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-700 mx-auto"></div>
          </div>
        ) : stats?.total_attempts === 0 ? (
          <div className="p-12 text-center bg-white rounded-2xl border border-stone-200" data-testid="progress-empty">
            <p className="text-stone-600 mb-3">Bu dönemde henüz sesli tekrar yapılmamış.</p>
            <button
              onClick={() => navigate('/elifba')}
              className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 font-semibold"
            >
              Hemen Başla
            </button>
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Target} label="Deneme" value={stats.total_attempts} color="bg-emerald-700" />
              <StatCard icon={Trophy} label="Doğru" value={stats.correct_attempts} color="bg-amber-600" />
              <StatCard icon={TrendingUp} label="Doğruluk" value={`%${stats.accuracy_percent}`} color="bg-teal-600" />
              <StatCard icon={Target} label="Çeşit" value={stats.unique_items} sub="farklı harf/hece" color="bg-purple-600" />
            </div>

            {/* Struggles + Strong */}
            <div className="grid md:grid-cols-2 gap-4">
              <ItemList
                title="Zorlandığı Harfler"
                items={stats.struggles || []}
                kids={isKidsMode}
                icon={AlertTriangle}
                emptyText="Çok güzel! Tüm harflerde başarılı."
                color={{
                  bg: 'bg-rose-50',
                  border: 'border-rose-200',
                  text: 'text-rose-800',
                  icon: 'text-rose-600',
                }}
              />
              <ItemList
                title="Başarılı Olduğu Harfler"
                items={stats.strong || []}
                kids={isKidsMode}
                icon={Trophy}
                emptyText="Daha fazla pratikle bu liste dolacak."
                color={{
                  bg: 'bg-emerald-50',
                  border: 'border-emerald-200',
                  text: 'text-emerald-800',
                  icon: 'text-emerald-600',
                }}
              />
            </div>

            {/* Full table */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-stone-200">
                <h3 className={`font-bold ${isKidsMode ? 'font-kids' : 'font-elegant'} text-stone-900`}>Tüm Denemeler</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="progress-full-table">
                  <thead className="bg-stone-50 text-stone-600">
                    <tr>
                      <th className="px-4 py-3 text-right">Harf</th>
                      <th className="px-4 py-3 text-right">Deneme</th>
                      <th className="px-4 py-3 text-right">Doğru</th>
                      <th className="px-4 py-3 text-right">Doğruluk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.per_item.map((p, i) => (
                      <tr key={i} className="border-t border-stone-100 hover:bg-stone-50">
                        <td className="px-4 py-3 text-right text-2xl font-arabic" dir="rtl">{p.expected}</td>
                        <td className="px-4 py-3 text-right">{p.attempts}</td>
                        <td className="px-4 py-3 text-right">{p.correct}</td>
                        <td className={`px-4 py-3 text-right font-bold ${p.accuracy >= 60 ? 'text-emerald-700' : 'text-rose-600'}`}>
                          %{p.accuracy}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default VoiceProgress;
